import { ApolloError } from '@apollo/client';
import { isEmpty } from 'lodash';
import { FC, useMemo } from 'react';
import { Comment as CommentType, User } from 't2-graphql-types';
import CommentContainer from './Comment/CommentContainer';
import { CommentsContext } from './CommentsContext';
import Spinner from '../Spinner/Spinner';
import { TCommentOrigin } from './types';
import { CommentsListType } from './constants';

export type TAuthor = Pick<User, 'id' | 'name' | 'publicAddress' | 'username' | 'profileImage'>;
export type TBaseCommentProps = Pick<
  CommentType,
  'content' | 'createdAt' | 'likes' | 'id' | 'likesCount' | 'childCommentsCount'
> & {
  author: TAuthor;
  parentComment?: { id: string };
  origin: TCommentOrigin;
  hasT2ChildComments?: boolean;
};

export type TCommentProps = TBaseCommentProps & {
  userAlreadyLikedComment: boolean;
  updateLike?: () => void;
};

export type TCommentsListProps = {
  comments: TCommentProps[] | undefined;
  userPublicAddress?: string;
  error?: ApolloError;
  loading: boolean;
  userId?: string;
  articleId: string;
  loadingAuthenticatedUserStatus: boolean;
  type?: CommentsListType;
};

const CommentsList: FC<TCommentsListProps> = ({
  userPublicAddress,
  articleId,
  error,
  comments,
  userId,
  loadingAuthenticatedUserStatus,
  loading,
  type = CommentsListType.FilteredComments,
}) => {
  const commentsContextValue = useMemo(
    () => ({
      userId,
      userPublicAddress,
      articleId,
      loadingAuthenticatedUserStatus,
    }),
    [userId, userPublicAddress, articleId, loadingAuthenticatedUserStatus],
  );

  if (loading) {
    return (
      <div className="mt-8 text-center">
        <Spinner />
      </div>
    );
  }

  if (error || isEmpty(comments)) {
    if (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }

    return null;
  }

  if (isEmpty(comments)) {
    return null;
  }

  return (
    <CommentsContext.Provider value={commentsContextValue}>
      {comments?.map(({ id, parentComment, author, ...props }, k) => {
        const isLastComment = k === comments.length - 1;
        const isCommentAuthor = userPublicAddress === author?.publicAddress;

        return (
          <CommentContainer
            {...props}
            author={author}
            parentCommentId={parentComment ? parentComment.id : undefined}
            id={id}
            key={id}
            isLastComment={isLastComment}
            isCommentAuthor={isCommentAuthor}
            commentsListType={type}
          />
        );
      })}
    </CommentsContext.Provider>
  );
};

export default CommentsList;
